import { all, put, select, takeEvery, take, call } from 'redux-saga/effects';
import { initialize, getFormValues, getFormNames, change } from 'redux-form';
import * as geolocationSelectors from 'modules/geolocation/selectors';
import * as geolocationActions from 'modules/geolocation/actions';
import {
  PERSONAL_DETAILS_FORM,
  CITY_RESTRICTOR_MODAL,
  COUNTRY_RESTRICTOR_MODAL,
  BANNED_COUNTRIES_MODAL,
} from '../constants';
import { PROFILE_DETAILS_FORM } from '../../../pages/ProfileData/constants';
import { getFormattedCountries } from 'modules/geolocation/selectors';
import * as actions from '../actions';
import * as migrationActionCreators from 'modules/migrateMyAccount/actions';
import * as signInActionCreators from 'modules/auth/pages/SignIn/actions';
import { push } from 'modules/reduxNavigation/actions';
import { getUserId } from 'modules/auth/selectors';
import * as userActions from 'modules/profile/pages/ProfileVerification/actions';
import { isOpen } from 'components/TP-UI/TPModal/selectors';
import { closeModal, openModal } from 'components/TP-UI/TPModal/actions';
import showErrorMessage from 'utils/showErrorMessage';
import { Routes } from 'constants/routeConstants';
import { getVerificationUserRequest } from 'modules/profile/pages/ProfileVerification/actions';
import { getUpdatedUserRequest } from 'modules/menu/actions';

function* checkLoadedCountries() {
  let countries = yield select(getFormattedCountries);
  if (countries.length === 0) {
    yield take(geolocationActions.getCountriesSuccess);
  }
  return;
}

function* findAndSetDefaultCountry() {
  yield take(userActions.getVerificationUserSuccess);
  yield take(actions.personalDetailsMount);
  const formNames = yield select(getFormNames());
  const formName =
    formNames.find((formName) => formName === PROFILE_DETAILS_FORM) ||
    formNames.find((formName) => formName === PERSONAL_DETAILS_FORM);
  if (!formName) {
    return;
  }
  yield call(checkLoadedCountries);
  const defaultCountry = yield select(geolocationSelectors.getCurrentLocation);
  if (!defaultCountry) {
    return;
  }

  const personalDataFormValues = yield select(getFormValues(formName));
  if (!personalDataFormValues) {
    const formData = {
      country: defaultCountry,
      phoneInfo: {
        phoneNumber: `+${defaultCountry.callingCodes[0]}`,
        phoneCountry: defaultCountry?.name,
      },
    };
    yield put(initialize(formName, formData));
  } else if (personalDataFormValues && !personalDataFormValues.country) {
    yield put(change(formName, 'country', defaultCountry));
  }
}

function* hideStateRestrictorHandler() {
  const formNames = yield select(getFormNames());
  const formName =
    formNames.find((formName) => formName === PROFILE_DETAILS_FORM) ||
    formNames.find((formName) => formName === PERSONAL_DETAILS_FORM);
  if (!formName) {
    return;
  }
  yield put(closeModal(CITY_RESTRICTOR_MODAL));
  yield put(change(formName, 'state', ''));
}

function* redirectToHomepageHandler() {
  const state = yield select();
  const isCityRestrictorModalOpen = isOpen(state, CITY_RESTRICTOR_MODAL);
  const isBannedCountriesModalOpen = isOpen(state, BANNED_COUNTRIES_MODAL);

  const modalNameToClose = isCityRestrictorModalOpen
    ? CITY_RESTRICTOR_MODAL
    : isBannedCountriesModalOpen
    ? BANNED_COUNTRIES_MODAL
    : null;
  yield put(closeModal(modalNameToClose));

  yield put(push(Routes.HOME));
}

function* showStateRestrictorHandler() {
  yield put(openModal(CITY_RESTRICTOR_MODAL));
}

function* showBannedCountryModal() {
  yield put(openModal(BANNED_COUNTRIES_MODAL));
}

function* hideBannedCountryModal() {
  const formNames = yield select(getFormNames());
  const formName =
    formNames.find((formName) => formName === PROFILE_DETAILS_FORM) ||
    formNames.find((formName) => formName === PERSONAL_DETAILS_FORM);
  if (!formName) {
    return;
  }
  yield put(closeModal(BANNED_COUNTRIES_MODAL));
  yield put(change(formName, 'country', ''));
  yield put(change(formName, 'tin', null));
}

function* migrateUserSuccess(action) {
  const { response } = action;
  yield put(signInActionCreators.updateToken(response));
  yield put(closeModal(COUNTRY_RESTRICTOR_MODAL));
  const userId = yield select(getUserId);
  yield put(getUpdatedUserRequest(userId));
  yield put(getVerificationUserRequest(userId));
}

function* closeMigrationConfirmation() {
  yield put(migrationActionCreators.confirmSuccessClose());
}

function* migrateUserFail(action) {
  yield call(showErrorMessage, action);
}

export default function* personalDetailsSaga() {
  yield all([
    takeEvery(
      [geolocationActions.getCountryByIpSuccess, geolocationActions.getCountryByIpFail],
      findAndSetDefaultCountry,
    ),
    takeEvery(actions.showStateRestrictor, showStateRestrictorHandler),
    takeEvery(actions.hideStateRestrictor, hideStateRestrictorHandler),
    takeEvery(actions.showBannedCountryModal, showBannedCountryModal),
    takeEvery(actions.hideBannedCountryModal, hideBannedCountryModal),
    takeEvery(actions.redirectToHomepage, redirectToHomepageHandler),
    takeEvery(migrationActionCreators.migrateUserSuccess, migrateUserSuccess),
    takeEvery(migrationActionCreators.migrateUserFail, migrateUserFail),
    takeEvery(actions.closeMigrationConfirmation, closeMigrationConfirmation),
  ]);
}
